import { PropsWithChildren, useEffect, useState } from 'react';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';

type PHProviderProps = PropsWithChildren<{
  posthogKey: string;
}>

export function PHProvider( { posthogKey, children }: PHProviderProps ) {
  const [ hydrated, setHydrated ] = useState( false );

  useEffect( () => {
    //assign 5% of our users to "advanced" cohort
    let stored: string | null = null;
    try {
      stored = localStorage.getItem( 'ph_advancedA' );
      if ( stored == null ) {
        stored = Math.random() < 0.30 ? 'true' : 'false';
        localStorage.setItem( 'ph_advancedA', stored );
      }
    } catch ( error ) {
      console.warn( 'localStorage unavailable, defaulting to non-advanced cohort:', error );
      stored = 'false';
    }
    const isAdvanced = stored === 'true';

    posthog.init( posthogKey, {
      api_host: process.env.NODE_ENV === 'development' ? 'https://eu.i.posthog.com' : '/relay-fEm8/',
      ui_host: 'https://eu.i.posthog.com',
      defaults: '2025-05-24',
      rageclick: true,
      capture_pageleave: false,
      capture_dead_clicks: true,
      capture_performance: false,
      autocapture: false,
      person_profiles: 'always',

      before_send: ( event ) => {
        // remove unwanted metadata
        if ( event?.properties ) {
          [ '$timezone', '$timezone_offset', '$ip' ].forEach( ( k ) => {
            delete event.properties[k];
          } );
        }
        //filter out "advancedOnly" events for non-advanced users
        if ( event?.properties?.advancedOnly && !isAdvanced ) {
          return null;
        }
        delete event?.properties?.advancedOnly;

        const trxTokenPattern = /TRX-[A-Z0-9]{25}-[A-Z0-9]{5}/g;
        const codeParamPattern = /([?&])code=[^&]+(&|$)/g;
        const keysToScrub = [ '$current_url', '$session_entry_url', '$initial_current_url', '$pathname', '$initial_pathname' ];

        if ( event ) {
          keysToScrub.forEach( ( key ) => {
            const raw = event.properties?.[key];
            if ( typeof raw !== 'string' ) {
              return;
            }

            const sanitized = raw
              .replace( trxTokenPattern, ':TRX' )
              .replace( codeParamPattern, ( _m, leading, trailing ) => trailing ? leading : '' );
            const [ urlBase, queryString ] = sanitized.split( '?' );
            event.properties[key] = urlBase;

            if ( queryString && key !== '$pathname' ) {
              const queryParams = new URLSearchParams( queryString );
              queryParams.forEach( ( value, name ) => {
                if ( name === 'code' ) {
                  return;
                }
                const cleaned = value.replace( trxTokenPattern, '' );
                if ( cleaned ) {
                  event.properties[name] = cleaned;
                }
              } );
            }
          } );
        }

        return event;
      },
    } );

    try {
      posthog.register( { ph_advancedA: isAdvanced } );
      if ( isAdvanced ) {
        posthog.people?.set( { ph_advancedA: true } );
      }
    } catch ( error ) {
      console.warn( 'Failed to register PostHog cohort:', error );
    }

    setHydrated( true )
  }, [ ] )

  if ( !hydrated ) {
    return <>{children}</>;
  }

  return <PostHogProvider client={ posthog }>{children}</PostHogProvider>;
}
